import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import FormInput from "../../../components/Form/FormInput";
import FormSelect from "../../../components/Form/FormSelect";
import FormTextarea from "../../../components/Form/TextArea";
import DateTimeField from "../../../components/Form/DateTimeField";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";

const PARTIES = {
  client: {
    title: "New client payment",
    endpoint: "/project-finances/client-payments",
    listPath: "/client/payments",
    detailPath: (id) => `/client/payment/${id}`,
    partyField: "client_id",
    partyLabel: "Client",
    partyList: "clients",
  },
  vendor: {
    title: "New vendor payment",
    endpoint: "/project-finances/vendor-payments",
    listPath: "/vendor/payments",
    detailPath: (id) => `/vendor/payment/${id}`,
    partyField: "vendor_id",
    partyLabel: "Vendor",
    partyList: "vendors",
  },
};

const VALUE_FORMAT = "YYYY-MM-DDTHH:mm";

const methodLabel = (m) => {
  const type = m.type?.replace(/_/g, " ");
  return m.name && m.name !== type ? `${m.name} (${type})` : type || m.name;
};

const NewPaymentPage = ({ party }) => {
  const config = PARTIES[party];
  const navigate = useNavigate();

  const [options, setOptions] = useState({ parties: [], methods: [], currencies: [] });
  const [form, setForm] = useState({
    party_id: "",
    payment_method_id: "",
    amount: "",
    currency_id: "",
    paid_at: dayjs().format(VALUE_FORMAT),
    note: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`${config.endpoint}/form-options`, { withCredentials: true })
      .then(({ data }) =>
        setOptions({
          parties: data.data[config.partyList] || [],
          methods: data.data.payment_methods || [],
          currencies: data.data.currencies || [],
        })
      )
      .catch(() => toast.error("Could not load the form options."))
      .finally(() => setLoading(false));
  }, [config]);

  // A vendor's payment methods are its own, so they load once a vendor is picked.
  useEffect(() => {
    if (party !== "vendor") return;
    setOptions((prev) => ({ ...prev, methods: [] }));
    if (!form.party_id) return;
    api
      .get(`${config.endpoint}/vendor-methods/${form.party_id}`, { withCredentials: true })
      .then(({ data }) => {
        const methods = data.data || [];
        setOptions((prev) => ({ ...prev, methods }));
        const preferred = methods.find((m) => m.is_default) || methods[0];
        setForm((prev) => ({ ...prev, payment_method_id: preferred ? preferred.id : "" }));
      })
      .catch(() => toast.error("Could not load the vendor's payment methods."));
  }, [party, config, form.party_id]);

  // The account's default payment method is preselected for clients.
  useEffect(() => {
    if (party !== "client" || form.payment_method_id) return;
    const preferred = options.methods.find((m) => m.is_default);
    if (preferred) setForm((prev) => ({ ...prev, payment_method_id: preferred.id }));
  }, [party, options.methods, form.payment_method_id]);

  const setField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "party_id") {
        if (party === "vendor") next.payment_method_id = "";
        // Start from the party's own currency when the account has it.
        const chosen = options.parties.find((p) => String(p.id) === String(value));
        const match = options.currencies.find((c) => c.currency_id === chosen?.default_currency_id);
        if (match) next.currency_id = match.id;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.party_id) next.party_id = `Select a ${config.partyLabel.toLowerCase()}.`;
    const amount = Number(form.amount);
    if (form.amount === "" || !Number.isFinite(amount) || amount <= 0) next.amount = "Enter an amount greater than 0.";
    if (!form.currency_id) next.currency_id = "Select a currency.";
    if (!form.paid_at) next.paid_at = "Select when it was paid.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        config.endpoint,
        {
          [config.partyField]: Number(form.party_id),
          payment_method_id: form.payment_method_id ? Number(form.payment_method_id) : null,
          amount: Number(form.amount),
          currency_id: Number(form.currency_id),
          paid_at: dayjs(form.paid_at).toISOString(),
          note: form.note,
        },
        { withCredentials: true }
      );
      toast.success(`Payment ${data.data.payment_code} created.`);
      navigate(config.detailPath(data.data.id));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to create the payment.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );

  const currencyCode = options.currencies.find((c) => String(c.id) === String(form.currency_id))?.code;
  const noVendorMethods = party === "vendor" && form.party_id && options.methods.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BackButton onClick={() => navigate(config.listPath)} />
        <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">{config.title}</h2>
      </div>

      <div className="bg-white shadow rounded-lg p-8">
        <div className="space-y-5 max-w-xl">
          <FormSelect
            label={config.partyLabel}
            name="party_id"
            value={form.party_id}
            onChange={setField}
            options={options.parties.map((p) => ({ value: p.id, label: p.name }))}
            error={errors.party_id}
            required
            disabled={saving}
          />
          <div>
            <FormSelect
              label="Payment method"
              name="payment_method_id"
              value={form.payment_method_id}
              onChange={setField}
              options={options.methods.map((m) => ({ value: m.id, label: methodLabel(m) }))}
              placeholder={party === "vendor" && !form.party_id ? "Select a vendor first" : undefined}
              disabled={saving || (party === "vendor" && !form.party_id)}
            />
            {noVendorMethods && (
              <p className="mt-1 text-xs text-gray-500">This vendor has no payment methods set up.</p>
            )}
          </div>
          <FormInput
            label="Amount"
            name="amount"
            type="number"
            value={form.amount}
            onChange={setField}
            error={errors.amount}
            required
            disabled={saving}
            suffix={currencyCode || undefined}
          />
          <FormSelect
            label="Currency"
            name="currency_id"
            value={form.currency_id}
            onChange={setField}
            options={options.currencies.map((c) => ({ value: c.id, label: c.name ? `${c.code} - ${c.name}` : c.code }))}
            error={errors.currency_id}
            required
            disabled={saving}
          />
          <DateTimeField
            label="Paid at"
            name="paid_at"
            value={form.paid_at}
            onChange={setField}
            error={errors.paid_at}
            required
            disabled={saving}
          />
          <FormTextarea label="Note" name="note" value={form.note} onChange={setField} disabled={saving} />
          <div className="flex gap-3">
            <button onClick={handleCreate} disabled={saving} className={btn("primary", "sm")}>
              {saving ? "Creating..." : "Create"}
            </button>
            <button onClick={() => navigate(config.listPath)} disabled={saving} className={btn("secondary", "sm")}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPaymentPage;
